"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { useSession } from "next-auth/react";

// ✅ EAGER (Above-the-fold)
import Hero1 from "@/components/hero/hero-1";

// ✅ Lazy-loaded (Below-the-fold, SEO-safe)
const Hotels = dynamic(() => import("@/components/hotels/Hotels"));

const BlockGuide = dynamic(() => import("@/components/block/BlockGuide"));

const Testimonial = dynamic(
  () => import("@/components/home/home-1/Testimonial"));

const TestimonialLeftCol = dynamic(
  () => import("@/components/home/home-1/TestimonialLeftCol"));

const Blog = dynamic(() => import("@/components/blog/Blog3"));

const CallToActions = dynamic(
  () => import("@/components/common/CallToActions"));

const SigninStrip = dynamic(
  () => import("@/components/home/SigninStrip/SigninStrip")
);

type HomeClientProps = {
  data: {
    featured_hotels: any[];
    featured_blogs: any[];
  };
};

export default function HomeClient({ data }: HomeClientProps) {
  const { data: session } = useSession();
  const { featured_hotels, featured_blogs } = data;

  return (
    <>
      {/* HERO (Critical for SEO & LCP) */}
      <div className="global_padding1 container">
        <Hero1 />
      </div>

      {/* Sign-in CTA (non-blocking) */}
      {!session && <SigninStrip />}

      {/* Recommended Hotels */}
      <section className={`layout-pb-md ${!session ? "mt-20" : "mt-50"}`}>
        <div className="container">
          <div className="row y-gap-10 justify-between items-end">
            <div className="col-auto">
              <div className="sectionTitle -md">
                <h2 className="sectionTitle__title">Recommended hotels with strong guest value</h2>
                <p className="sectionTitle__text mt-5 sm:mt-0">
                  Handpicked stays with real reviews, clear pricing, and Roave support when you need it.
                </p>
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden pt-40 sm:pt-20">
            <Hotels data={featured_hotels} />
          </div>
        </div>
      </section>

      <div className="container">
        <div className="border-top-light" />
      </div>

      {/* Block Guide */}
      <section className="layout-pt-md layout-pb-lg">
        <div className="container">
          <div className="row y-gap-20 justify-between">
            <BlockGuide />
          </div>
        </div>
      </section>

      <div className="container">
        <div className="border-top-light" />
      </div>

      {/* Testimonials */}
      <section className="layout-pt-lg layout-pb-lg">
        <div className="container">
          <div className="row y-gap-40 justify-between">
            <div className="col-xl-5 col-lg-6">
              <TestimonialLeftCol />
            </div>

            <div className="col-lg-6 overflow-hidden">
              <Testimonial />
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        <div className="border-top-light" />
      </div>

      {/* Blogs */}
      <section className="layout-pt-lg layout-pb-md">
        <div className="container">
          <div className="row justify-center text-center">
            <div className="col-auto">
              <div className="sectionTitle -md">
                <h2 className="sectionTitle__title">
                  Plan smarter before you book
                </h2>
                <p className="sectionTitle__text mt-5 sm:mt-0">
                  Fresh guides, timing tips, and destination ideas to help you choose with confidence.
                </p>
              </div>
            </div>
          </div>

          <div className="row justify-end text-center">
            <div className="col-auto text-right">
              <Link
                href="/blog"
                className="button -md -blue-1 bg-blue-1-05 text-blue-1"
              >
                Read travel guides
              </Link>
            </div>
          </div>

          <div className="row y-gap-30 pt-40 mb-20">
            <Blog featured_blogs={featured_blogs} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <CallToActions />
    </>
  );
}
